import { Color } from '@persona-id/design-tokens';
import { FC, SVGAttributes } from 'react';
import styled, { keyframes } from 'styled-components';

import { BaseEdgeProps } from 'dashboard/components-lib/FlowPlotter';

const strokeStyle = (active: boolean = false): SVGAttributes<SVGPathElement> => ({
  stroke: active ? Color.CorePurple800 : Color.CorePurple200,
  strokeWidth: 2,
  fill: 'transparent',
});

interface WorkflowEdgeProps extends BaseEdgeProps {
  active: boolean;
}

const breatheAnimation = keyframes`
  0% { opacity: 0 }
  100% { opacity: 1 }
`;

const Path = styled.path`
  animation: ${breatheAnimation} 1.5s 1;
`;

export const PathLeanVerticallyFirst: FC<WorkflowEdgeProps> = ({
  active,
  fromX,
  fromY,
  toX,
  toY,
}) => {
  const path =
    Math.abs(fromX - toX) < 0.5
      ? `L ${toX} ${toY}`
      : (() => {
          const goLeft = fromX > toX;
          const leanVertically = `L ${fromX} ${toY - 36}`;
          const firstCorner = `Q ${fromX} ${toY - 24}, ${fromX + (goLeft ? -12 : 12)} ${toY - 24}`;
          const leanHorizontally = `L ${toX + (goLeft ? 12 : -12)} ${toY - 24}`;
          const finalCorner = `Q ${toX} ${toY - 24}, ${toX} ${toY - 12}`;
          const runInto = `L ${toX} ${toY}`;

          return [leanVertically, firstCorner, leanHorizontally, finalCorner, runInto].join(' ');
        })();

  return <Path {...strokeStyle(active)} d={`M ${fromX} ${fromY} ${path}`} />;
};

export const PathLeanHorizontallyFirst = ({ fromX, fromY, toX, toY }: BaseEdgeProps) => {
  const path =
    fromY === toY
      ? `L ${toX} ${toY}`
      : (() => {
          const goUp = fromY > toX;
          const leanHorizontally = `L ${toX - 36} ${fromY}`;
          const firstCorner = `Q ${toX - 24} ${fromY}, ${toX - 24}  ${fromY + (goUp ? -12 : 12)}`;
          const leanVertically = `L ${toX - 24} ${toY + (goUp ? 12 : -12)}`;
          const finalCorner = `Q ${toX - 24} ${toY}, ${toX - 12} ${toY}`;
          const runInto = `L ${toX} ${toY}`;

          return [leanHorizontally, firstCorner, leanVertically, finalCorner, runInto].join(' ');
        })();

  return <path {...strokeStyle()} d={`M ${fromX} ${fromY} ${path}`} />;
};

export const LoopBackArrow: FC<
  { active: boolean; fromXToLeftVertical: number } & BaseEdgeProps
> = ({ active, fromXToLeftVertical, fromX, fromY, toX, toY }) => {
  const down = `L ${fromX} ${fromY + 24}`;
  const firstCorner = `Q ${fromX} ${fromY + 36}, ${fromX - 12} ${fromY + 36}`;
  const left = `L ${fromX - fromXToLeftVertical + 12} ${fromY + 36}`;
  const secondCorner = `Q ${fromX - fromXToLeftVertical} ${fromY + 36}, ${
    fromX - fromXToLeftVertical
  } ${fromY + 24}`;
  const up = `L ${fromX - fromXToLeftVertical} ${toY + 48}`;
  const thirdCorner = `Q ${fromX - fromXToLeftVertical} ${toY + 36}, ${
    fromX - fromXToLeftVertical + 12
  } ${toY + 36}`;
  const converge = `L ${toX - 15} ${toY + 36}`;

  const path = [down, firstCorner, left, secondCorner, up, thirdCorner, converge].join(' ');

  return (
    <>
      <defs>
        <marker
          id='arrow'
          viewBox='-10 -10 20 20'
          orient='auto-start-reverse'
          refX='0'
          refY='0'
          markerWidth='15'
          markerHeight='15'
          markerUnits='strokeWidth'
        >
          <polyline
            {...strokeStyle(active)}
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='1.5'
            fill='none'
            points='-4,-4 0,0 -4,4'
          />
        </marker>
      </defs>
      <path {...strokeStyle(active)} d={`M ${fromX} ${fromY} ${path}`} markerEnd='url(#arrow)' />
    </>
  );
};
